import { z } from 'zod/v4';
import {
  CreateOrderLineInputSchema,
  DashboardCreateOrderLineInputSchema,
  DashboardUpsertOrderLineInputSchema,
  ORDER_ACTION_VALUES,
  ORDER_EXTERNAL_ID_MAX_LENGTH,
  StylePropertiesSchema,
  UpsertOrderLineInputSchema,
} from './order-line.js';

export { ORDER_ACTION_VALUES, type OrderAction } from './order-line.js';

export const ORDER_TYPE_VALUES = [
  'PURCHASE_ORDER',
  'SALES_ORDER',
  'INTERNAL_ORDER',
] as const;

export type OrderType = (typeof ORDER_TYPE_VALUES)[number];

export const ORDER_SUB_TYPE_VALUES = [
  'STANDARD',
  'REPEAT',
  'COMMIT',
  'SAMPLE',
  'EXTRACTION_SPLIT',
] as const;

export type OrderSubType = (typeof ORDER_SUB_TYPE_VALUES)[number];

export const ORDER_STATUS_VALUES = [
  'IN_PROGRESS',
  'IN_REVIEW',
  'COMPLETED',
] as const;

export const ORDER_SOURCE = ['PUBLIC_API', 'IMPORTER', 'INTERNAL_API'] as const;
export type OrderSource = (typeof ORDER_SOURCE)[number];

export type OrderStatus = (typeof ORDER_STATUS_VALUES)[number];

export const TRACING_REQUEST_STATUS_VALUES = [
  'IN_PROGRESS',
  'IN_REVIEW',
  'COMPLETED',
] as const;

/** Multi-select filter value that matches orders where the attribute is empty. */
export const FILTER_NO_VALUE = 'N/A';

export const DELIVERY_DATE_VARIANTS = ['agreed', 'handover', 'actual'] as const;

export type DeliveryDateVariant = (typeof DELIVERY_DATE_VARIANTS)[number];

export const SupplierCountryOptionSchema = z.object({
  code: z.string(),
  name: z.string().nullable(),
});

export type SupplierCountryOption = z.infer<typeof SupplierCountryOptionSchema>;

export const OrderResourceSchema = z
  .object({
    id: z.string(),
    forCompanyId: z.string(),
    externalId: z.string().nullable(),
    orderNumber: z.string(),
    orderType: z.enum(ORDER_TYPE_VALUES),
    // `orderSubType` / `status` are validated as enums on write (create/update)
    // but kept lenient on read so legacy/pre-enum rows never fail serialization.
    orderSubType: z.string().nullable(),
    status: z.string().nullable(),
    tags: z.array(z.string()),
    source: z.string().nullable(),
    buyerCompanyId: z.string(),
    supplierCompanyId: z.string(),
    receiverCompanyId: z.string(),
    vendorCompanyId: z.string().nullable(),
    factory: z.string().nullable(),
    buyerFacilityProcess: z.string().nullable(),
    supplierFacilityProcess: z.string().nullable(),
    receiverFacilityProcess: z.string().nullable(),
    orderDate: z.string().nullable(),
    deliveryDate: z.string().nullable(),
    agreedDeliveryDate: z.string().nullable(),
    expectedShippingDate: z.string().nullable(),
    shippingDate: z.string().nullable(),
    handoverDate: z.string().nullable(),
    actualDeliveryDate: z.string().nullable(),
    shipmentReference: z.string().nullable(),
    internalResponsiblePersons: z.array(z.string()).default([]),
    styleProperties: StylePropertiesSchema.optional(),
    counterpartyOrderNumberRef: z.string().nullable(),
    tracingAttachmentTemplateId: z.string().nullable(),
    isArchived: z.boolean(),
    tracingStatus: z.string().nullable(),
    /** List only: status of the Tracing request sent from or referenced by the order. */
    tracingRequestStatus: z.string().nullable().optional(),
    /** List only: country of the supplier company. */
    supplierCountry: SupplierCountryOptionSchema.nullable().optional(),
    createdAt: z.string().nullable(),
    createdByUserId: z.string().nullable(),
    createdByUserName: z.string().nullable(),
    createdByCompanyId: z.string().nullable(),
    createdByCompanyName: z.string().nullable(),
    createdByImpersonateId: z.string().nullable(),
    createdByImpersonateName: z.string().nullable(),
    createdByApiKeyId: z.string().nullable(),
    createdByApiKeyLabel: z.string().nullable(),
    updatedAt: z.string().nullable(),
    updatedByUserId: z.string().nullable(),
    updatedByUserName: z.string().nullable(),
    updatedByCompanyId: z.string().nullable(),
    updatedByCompanyName: z.string().nullable(),
    updatedByImpersonateId: z.string().nullable(),
    updatedByImpersonateName: z.string().nullable(),
    updatedByApiKeyId: z.string().nullable(),
    updatedByApiKeyLabel: z.string().nullable(),
    lineCount: z.number().int(),
    permissions: z.array(z.enum(ORDER_ACTION_VALUES)).optional(),
    /** Detail only: the Tracing request linked by hand or by a reference. */
    tracingLink: z
      .object({
        kind: z.enum(['OWNER', 'RECEIVED']),
        tracingOrderId: z.string(),
        requestId: z.string().nullable(),
        requestNumber: z.string().nullable(),
      })
      .nullable()
      .optional(),
    lines: z
      .array(
        z.object({
          id: z.string(),
          externalId: z.string().nullable(),
          orderLineNumber: z.string().nullable(),
          lineNo: z.number().int().nullable(),
          styleId: z.string(),
          quantity: z.number(),
          unit: z.string(),
          secondaryQuantity: z.number().nullable(),
          secondaryUnit: z.string().nullable(),
          tracingReflectionOrderId: z.string().nullable(),
          tracingOrderRequestedAt: z.string().nullable(),
          cmmOrderLineId: z.string().nullable(),
        }),
      )
      .default([]),
  })
  .strict();

export type OrderResource = z.infer<typeof OrderResourceSchema>;

const str = z.string().max(255);
const idStr = z.string().max(36);
const externalIdStr = z
  .string()
  .trim()
  .min(1)
  .max(ORDER_EXTERNAL_ID_MAX_LENGTH);

// Strict ISO calendar date (YYYY-MM-DD). We keep the full `z.iso.date()`
// validation but override its JSON-Schema output: the auto-generated RFC 3339
// `pattern` is enormous and unreadable in the API docs, so we drop it and
// surface the friendlier `format: date` + an example instead.
const OrderDateSchema = z.iso.date().meta({
  pattern: undefined,
  format: 'date',
  examples: ['2026-07-21'],
});

export const CreateOrderInputSchema = z
  .object({
    externalId: externalIdStr.optional(),
    orderNumber: str.min(1),
    orderSubType: z.enum(ORDER_SUB_TYPE_VALUES).optional(),
    status: z.enum(ORDER_STATUS_VALUES).optional(),
    tags: z.array(z.string()).optional(),
    source: z.enum(ORDER_SOURCE).default('INTERNAL_API'),
    buyerCompanyId: idStr.min(1),
    supplierCompanyId: idStr.min(1),
    receiverCompanyId: idStr.optional(),
    vendorCompanyId: idStr.optional(),
    factory: idStr.optional(),
    buyerFacilityProcess: str.optional(),
    supplierFacilityProcess: str.optional(),
    receiverFacilityProcess: str.optional(),
    orderDate: OrderDateSchema.optional(),
    deliveryDate: OrderDateSchema.optional(),
    agreedDeliveryDate: OrderDateSchema.optional(),
    expectedShippingDate: OrderDateSchema.optional(),
    shippingDate: OrderDateSchema.optional(),
    handoverDate: OrderDateSchema.optional(),
    actualDeliveryDate: OrderDateSchema.optional(),
    shipmentReference: str.optional(),
    // Each entry accepts an email OR a userId; the service resolves each to a
    // userId before persistence, so allow up to email length (255).
    internalResponsiblePersons: z.array(str).optional(),
    counterpartyOrderNumberRef: z.string().optional(),
    tracingAttachmentTemplateId: idStr.min(1).nullish(),
    orderType: z.enum(ORDER_TYPE_VALUES).nullable().optional(),
    tracingStatus: str.nullable().optional(),
    isArchived: z.boolean().optional(),
  })
  .strict();

export type CreateOrderInput = z.infer<typeof CreateOrderInputSchema>;

function requireReceiverPair(
  val: { receiverCompanyId?: string; receiverFacilityProcess?: string },
  ctx: z.RefinementCtx,
): void {
  const hasCompany = val.receiverCompanyId !== undefined;
  const hasProcess = val.receiverFacilityProcess !== undefined;
  if (hasCompany !== hasProcess) {
    ctx.addIssue({
      code: 'custom',
      path: [hasCompany ? 'receiverFacilityProcess' : 'receiverCompanyId'],
      message:
        'receiverCompanyId and receiverFacilityProcess must both be provided when a receiver is set',
    });
  }
}

export const CreateOrderRequestSchema = CreateOrderInputSchema.extend({
  lines: z.array(CreateOrderLineInputSchema).optional(),
})
  .strict()
  .superRefine(requireReceiverPair);

export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>;

export const UpdateOrderInputSchema = CreateOrderInputSchema.partial()
  .omit({ source: true })
  .extend({
    externalId: externalIdStr.nullable().optional(),
    orderSubType: z.enum(ORDER_SUB_TYPE_VALUES).nullable().optional(),
    status: z.enum(ORDER_STATUS_VALUES).nullable().optional(),
    buyerCompanyId: idStr.min(1).optional(),
    supplierCompanyId: idStr.min(1).optional(),
    receiverCompanyId: idStr.nullable().optional(),
    vendorCompanyId: idStr.nullable().optional(),
    factory: idStr.nullable().optional(),
    buyerFacilityProcess: str.nullable().optional(),
    supplierFacilityProcess: str.nullable().optional(),
    receiverFacilityProcess: str.nullable().optional(),
    orderDate: OrderDateSchema.nullable().optional(),
    deliveryDate: OrderDateSchema.nullable().optional(),
    agreedDeliveryDate: OrderDateSchema.nullable().optional(),
    expectedShippingDate: OrderDateSchema.nullable().optional(),
    shippingDate: OrderDateSchema.nullable().optional(),
    handoverDate: OrderDateSchema.nullable().optional(),
    actualDeliveryDate: OrderDateSchema.nullable().optional(),
    shipmentReference: str.nullable().optional(),
    internalResponsiblePersons: z.array(str).optional().nullable(),
    counterpartyOrderNumberRef: z.string().nullable().optional(),
    tracingAttachmentTemplateId: idStr.min(1).nullable().optional(),
    isArchived: z.boolean().optional(),
    lines: z.array(UpsertOrderLineInputSchema).optional(),
  });

export type UpdateOrderInput = z.infer<typeof UpdateOrderInputSchema>;

/** Dashboard order inputs: `externalId` is written through the Public API only. */
export const DashboardCreateOrderInputSchema = CreateOrderInputSchema.omit({
  externalId: true,
});

export const DashboardCreateOrderRequestSchema =
  DashboardCreateOrderInputSchema.extend({
    lines: z.array(DashboardCreateOrderLineInputSchema).optional(),
  })
    .strict()
    .superRefine(requireReceiverPair);

export const DashboardUpdateOrderInputSchema = UpdateOrderInputSchema.omit({
  externalId: true,
}).extend({
  lines: z.array(DashboardUpsertOrderLineInputSchema).optional(),
});

export const BULK_UPDATE_ORDERS_MAX = 100;

export const BulkOrderPatchSchema = z
  .object({
    isArchived: z.boolean().optional(),
    status: z.enum(ORDER_STATUS_VALUES).optional(),
    tracingAttachmentTemplateId: idStr.min(1).nullable().optional(),
    tags: UpdateOrderInputSchema.shape.tags,
    internalResponsiblePersons: z.array(str).optional(),
    orderSubType: UpdateOrderInputSchema.shape.orderSubType,
  })
  .strict()
  .refine(patch => Object.keys(patch).length > 0, {
    message: 'patch must contain at least one field',
  });

export type BulkOrderPatch = z.infer<typeof BulkOrderPatchSchema>;

export const BulkUpdateOrdersInputSchema = z.object({
  ids: z.array(idStr.min(1)).min(1).max(BULK_UPDATE_ORDERS_MAX),
  patch: BulkOrderPatchSchema,
});

export type BulkUpdateOrdersInput = z.infer<typeof BulkUpdateOrdersInputSchema>;

export const BulkUpdateOrdersResultSchema = z.object({
  updatedCount: z.number().int(),
  failedIds: z.array(z.string()),
});

export type BulkUpdateOrdersResult = z.infer<
  typeof BulkUpdateOrdersResultSchema
>;

export const ORDER_SORT_FIELDS = [
  'updatedAt',
  'createdAt',
  'orderNumber',
  'orderDate',
  'status',
] as const;
